import type { Api } from '@shopexperts/legacy-api';
import { aiSummariesErrorMessage } from './aiSummaries';

export interface ExpertBulkActionResult {
  processedIds: number[];
  message: string;
  error?: string;
}

export interface ExpertBulkAction {
  id: string;
  label: string;
  description: string;
  supportsOnlyMissing?: boolean;
  execute: (
    expertIds: readonly number[],
    context: { api: Api; onProgress: (message: string) => void; onlyMissing?: boolean; onBatchQueued?: (batchId: string) => void },
  ) => Promise<ExpertBulkActionResult>;
}

/** Register new Expert Manager bulk actions here. */
export const expertBulkActions: readonly ExpertBulkAction[] = [
  {
    id: 'generate-ai-summaries',
    label: 'Generate AI summaries',
    description: 'Generate new AI summaries for the selected experts.',
    supportsOnlyMissing: true,
    async execute(expertIds, { api, onProgress, onlyMissing = true, onBatchQueued }) {
      const processedIds: number[] = [];
      let queued = 0;
      let skipped = 0;
      for (let offset = 0; offset < expertIds.length; offset += 100) {
        const chunk = expertIds.slice(offset, offset + 100);
        onProgress(`Queueing summaries… ${processedIds.length.toLocaleString()} of ${expertIds.length.toLocaleString()} experts`);
        try {
          const result = await api.admin.expertAiSummaries.generateBatch({ body: { expert_ids: chunk, only_missing: onlyMissing } });
          if (result.batch_id) onBatchQueued?.(result.batch_id);
          queued += result.total;
          skipped += result.skipped_expert_ids.length;
          processedIds.push(...chunk);
        } catch (error) {
          return {
            processedIds,
            message: `Generation queued for ${queued.toLocaleString()} experts.`,
            error: `${aiSummariesErrorMessage(error)} The remaining ${expertIds.length - processedIds.length} experts are still selected. Check their AI Summaries before retrying if the request timed out.`,
          };
        }
      }
      return {
        processedIds,
        message: `Generation queued for ${queued.toLocaleString()} experts.${skipped ? ` ${skipped} skipped.` : ''} Follow progress in AI summary jobs below.`,
      };
    },
  },
];
